/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import Component from '@glimmer/component';
import {
  DataFlow,
  IntegrationCategory,
  IntegrationIDs,
  IntegrationType,
  StaticIntegration,
} from 'cio/utils/integrations/static-integrations';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import Campaign, { CampaignType } from 'cio/models/campaign';

interface IntegrationsFeaturedArgs {
  integrations: StaticIntegration[];
  campaigns?: Campaign[];
}

type IntegrationGroup = {
  title: string;
  integrations: string[];
  isPremium?: boolean;
};
type PickedModalGroup = {
  title: string;
  integrations: StaticIntegration[];
};

export default class IntegrationsFeatured extends Component<IntegrationsFeaturedArgs> {
  @service analytics!: AnalyticsService;

  @tracked showGroupModal = false;
  @tracked groupsToShowInModal: StaticIntegration[] | null = null;
  @tracked pickedModalGroup: PickedModalGroup | null = null;
  @tracked showLearnAboutWebhooksModalVideo = false;

  readonly modalGroups: Record<DataFlow, IntegrationGroup[]> = {
    in: [
      {
        title: 'Connect your database',
        integrations: [
          IntegrationIDs.mysql,
          IntegrationIDs.postgres,
          IntegrationIDs.snowflakeIn,
          IntegrationIDs.redshiftIn,
          IntegrationIDs.bigQueryIn,
          IntegrationIDs.sqlserver,
        ],
      },
    ],
    out: [
      {
        title: 'Create audiences in ad networks',
        integrations: [
          IntegrationIDs.facebook,
          IntegrationIDs.instagram,
          IntegrationIDs.adwords,
          IntegrationIDs.youtube,
        ],
      },
      {
        title: 'Send data to your analytics platform',
        integrations: this.args.integrations
          .filter(
            ({ type, category }) =>
              type === IntegrationType.nativeDataOut && category === IntegrationCategory.analytics,
          )
          .map(({ id }) => id),
      },
      {
        title: 'Update your data warehouse',
        integrations: [
          IntegrationIDs.redshiftOut,
          IntegrationIDs.amazonS3,
          IntegrationIDs.azureBlob,
          IntegrationIDs.bigQueryOut,
          IntegrationIDs.gcs,
          IntegrationIDs.snowflakeOut,
          IntegrationIDs.yandex,
        ],
        isPremium: true,
      },
    ],
  };

  readonly DataFlow = DataFlow;

  get modalGroupsWithoutEnabledIntegrations() {
    return Object.entries(this.modalGroups).reduce(
      (aggr, entry: [DataFlow, IntegrationGroup[]]) => {
        let [dataFlow, groups] = entry;

        aggr[dataFlow] = groups.filter(
          (group) =>
            !group.integrations.find(
              (integrationID: string) => this.args.integrations.findBy('id', integrationID)?.enabled,
            ),
        );

        return aggr;
      },
      {} as Record<DataFlow, IntegrationGroup[]>,
    );
  }

  integrationsSort = ['weight:desc', 'name'];
  @sort('enabledDataInIntegrations', 'integrationsSort') sortedEnabledDataInIntegrations!: StaticIntegration[];
  @sort('enabledDataOutIntegrations', 'integrationsSort') sortedEnabledDataOutIntegrations!: StaticIntegration[];
  @sort('availableDataInIntegrations', 'integrationsSort') sortedAvailableDataInIntegrations!: StaticIntegration[];
  @sort('availableDataOutIntegrations', 'integrationsSort') sortedAvailableDataOutIntegrations!: StaticIntegration[];

  get enabledDataInIntegrations() {
    return this.args.integrations.filter(
      (integration) => integration.dataFlow === DataFlow.in && integration.enabled && !integration.hideFromFeatured,
    );
  }

  get availableDataInIntegrations() {
    return this.args.integrations.filter(
      (integration) => integration.dataFlow === DataFlow.in && !integration.enabled && integration.highlight,
    );
  }

  get enabledDataOutIntegrations() {
    return this.args.integrations.filter(
      (integration) => integration.dataFlow === DataFlow.out && integration.enabled && !integration.hideFromFeatured,
    );
  }

  get availableDataOutIntegrations() {
    return this.args.integrations.filter(
      (integration) => integration.dataFlow === DataFlow.out && !integration.enabled && integration.highlight,
    );
  }

  get showDataWebhooksFeaturedBlock() {
    return !this.args.campaigns?.some((campaign) => campaign.type === CampaignType.Webhook);
  }

  @action
  trackIntegrationClick(integration: StaticIntegration) {
    let trackData: Record<string, string | boolean | undefined> = {
      from: 'In-App',
      name: integration.name,
      category: integration.category,
      enabled: integration.enabled,
      type: integration.type,
    };

    if (integration.subtitle) {
      trackData.subtitle = integration.subtitle;
    }

    this.analytics.track('integration', 'clicked', trackData);
  }

  @action
  toggleShowGroupModal(dataGroup?: DataFlow, strIndex?: string) {
    if (dataGroup == null && strIndex == null) {
      this.groupsToShowInModal = null;
      return;
    }

    if (dataGroup == null || strIndex == null) {
      this.pickedModalGroup = null;
      return;
    }

    let index = Number(strIndex);
    let integrationIDs = this.modalGroupsWithoutEnabledIntegrations[dataGroup][index].integrations;
    let integrations = this.args.integrations.filter((int) => integrationIDs.includes(int.id));
    this.pickedModalGroup = {
      title: this.modalGroupsWithoutEnabledIntegrations[dataGroup][index].title,
      integrations,
    };
    return;
  }

  @action
  toggleLearnAboutWebhooksModalVideo() {
    if (!this.showLearnAboutWebhooksModalVideo) {
      this.analytics.track('integration', 'data_campaign_learn_how_clicked');
      this.showLearnAboutWebhooksModalVideo = true;
      return;
    }
    this.showLearnAboutWebhooksModalVideo = false;
  }

  @action
  trackLearnAboutWebhooksModalVideo() {
    this.analytics.track('integration', 'data_campaign_video_clicked', { video: true });
  }

  @action
  trackLearnAboutWebhooksModalClick(destination: string) {
    this.analytics.track('integration', 'data_campaign_example_clicked', { destination });
  }

  @tracked container: HTMLDivElement | null = null;

  @action
  didInsertContainer(container: HTMLDivElement) {
    this.container = container;
  }
}
